import React, { useState, useEffect } from 'react';
import { FaEdit, FaPlusCircle, FaExclamationTriangle, FaEye, FaEyeSlash } from 'react-icons/fa';
import PaymentInstructionService, { PaymentInstruction } from '../services/PaymentInstructionService';
import PaymentInstructionModal from './PaymentInstructionModal';
import { personalakteStyles as ps, colors } from '../styles/personalakteStyles';
import httpService from '../../../core/services/HttpService';

interface PaymentInstructionSectionProps {
  employeeId: number;
  employeeName: string;
}

const PaymentInstructionSection: React.FC<PaymentInstructionSectionProps> = ({
  employeeId,
  employeeName,
}) => {
  const [paymentInstruction, setPaymentInstruction] = useState<PaymentInstruction | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [revealedIban, setRevealedIban] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);

  useEffect(() => {
    loadPaymentInstruction();
  }, [employeeId]);

  const loadPaymentInstruction = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const data = await PaymentInstructionService.getPaymentInstruction(employeeId);
      setPaymentInstruction(data);
    } catch (error: any) {
      console.warn('[PaymentInstruction] Failed to load:', error?.response?.status);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    await loadPaymentInstruction();
    setRevealedIban(null);
    setShowModal(false);
  };

  const handleRevealIban = async () => {
    if (revealedIban) {
      setRevealedIban(null);
      return;
    }
    setRevealing(true);
    try {
      const { data } = await httpService.get<{ iban: string }>(
        `/api/admin/employees/${employeeId}/payment-instruction/reveal/`
      );
      setRevealedIban(data.iban);
    } catch {
      setRevealedIban(null);
    } finally {
      setRevealing(false);
    }
  };

  const sectionHeaderStyle: React.CSSProperties = {
    fontSize: 15,
    fontWeight: 600,
    color: colors.navy,
    margin: '0 0 16px 0',
    paddingBottom: 10,
    borderBottom: `1px solid ${colors.border}`,
  };

  const readOnlyValue: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 500,
    color: colors.textPrimary,
  };

  if (loading) {
    return (
      <div>
        <h3 style={sectionHeaderStyle}>Bank</h3>
        <div style={{ padding: 20, textAlign: 'center', color: colors.textMuted, fontSize: 13 }}>
          Laden...
        </div>
      </div>
    );
  }

  return (
    <>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 10, borderBottom: `1px solid ${colors.border}` }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.navy, margin: 0 }}>
            Bank
          </h3>
          <button
            type="button"
            className="hover-back"
            style={{
              ...ps.backButton,
              marginBottom: 0,
              fontSize: 11,
              padding: '4px 12px',
              color: colors.navy,
            }}
            onClick={() => setShowModal(true)}
          >
            {paymentInstruction ? <><FaEdit size={10} /> Bearbeiten</> : <><FaPlusCircle size={10} /> Hinzufügen</>}
          </button>
        </div>

        {loadError ? (
          <div style={{ ...ps.fieldWrapper, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaExclamationTriangle size={12} color={colors.warning} />
            <span style={{ fontSize: 13, color: colors.textMuted }}>
              Bankdaten konnten nicht geladen werden.{' '}
              <button
                type="button"
                onClick={loadPaymentInstruction}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.slate,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: 13,
                  padding: 0,
                  fontWeight: 500,
                }}
              >
                Erneut versuchen
              </button>
            </span>
          </div>
        ) : paymentInstruction ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={ps.fieldWrapper}>
                <label style={ps.labelStyle}>Kontoinhaber</label>
                <div style={readOnlyValue}>{paymentInstruction.holder_name || employeeName}</div>
              </div>
              <div style={ps.fieldWrapper}>
                <label style={ps.labelStyle}>IBAN</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ ...readOnlyValue, fontFamily: 'monospace', letterSpacing: '0.05em', flex: 1 }}>
                    {revealedIban || paymentInstruction.iban_masked || '—'}
                  </div>
                  {paymentInstruction.iban_masked && (
                    <button
                      type="button"
                      onClick={handleRevealIban}
                      disabled={revealing}
                      title={revealedIban ? 'IBAN verbergen' : 'IBAN anzeigen'}
                      style={{
                        background: 'none',
                        border: `1px solid ${colors.border}`,
                        borderRadius: '50%',
                        width: 28,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: revealing ? 'wait' : 'pointer',
                        color: revealedIban ? colors.info : colors.textMuted,
                        transition: 'all 0.15s',
                        flexShrink: 0,
                      }}
                    >
                      {revealedIban ? <FaEyeSlash size={12} /> : <FaEye size={12} />}
                    </button>
                  )}
                </div>
              </div>
            </div>
            {(paymentInstruction.bic || paymentInstruction.bank_name) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={ps.fieldWrapper}>
                  <label style={ps.labelStyle}>BIC</label>
                  <div style={readOnlyValue}>{paymentInstruction.bic || '—'}</div>
                </div>
                <div style={ps.fieldWrapper}>
                  <label style={ps.labelStyle}>Bank</label>
                  <div style={readOnlyValue}>{paymentInstruction.bank_name || '—'}</div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ ...ps.fieldWrapper, textAlign: 'center' }}>
            <span style={{ fontSize: 13, color: colors.textMuted }}>Keine Bankdaten hinterlegt</span>
          </div>
        )}
      </div>

      {showModal && (
        <PaymentInstructionModal
          employeeId={employeeId}
          employeeName={employeeName}
          existingInstruction={paymentInstruction}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </>
  );
};

export default PaymentInstructionSection;
